import { useMemo, useRef, useState, type ReactNode } from "react";
import { Spinner } from "@fluentui/react-components";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import { useCompareSlides } from "../hooks/useCompareSlides";
import { deleteShapeByName, deleteSlideAt, setShapeText } from "../services/deckWriteback";
import type { CellDiff, DiffHunk, ShapeResult, SlideComparison } from "../services/textComparer";
import styles from "./CompareSlides.module.css";

interface CompareSlidesProps {
  readonly onBack?: () => void;
}

/** Largest .pptx accepted for comparison. */
const MAX_FILE_BYTES = 200 * 1024 * 1024;

type Decision = "pending" | "reverted" | "kept";
type Tab = "all" | "pending" | "reverted" | "kept";

/**
 * What reverting a change means for the live deck:
 *   - modify        → restore the original text
 *   - add-shape     → delete the newly added shape
 *   - add-slide     → delete the newly added slide
 *   - delete-shape  → nothing (re-adding a removed shape isn't supported)
 *   - remove-slide  → nothing (re-adding a removed slide isn't supported)
 * The last two are review-only: they can be acknowledged (Keep) but not reverted.
 */
type ItemKind = "modify" | "add-shape" | "delete-shape" | "add-slide" | "remove-slide";

const TAB_LABEL: Record<Tab, string> = {
  all: "All",
  pending: "Pending",
  reverted: "Reverted",
  kept: "Kept",
};

const BADGE: Record<SlideComparison["status"], { label: string; cls: string }> = {
  modified: { label: "CHANGED", cls: "badgeChanged" },
  added: { label: "ADDED", cls: "badgeAdded" },
  removed: { label: "REMOVED", cls: "badgeRemoved" },
  unchanged: { label: "UNCHANGED", cls: "" },
};

/** A single reviewable change — one edited shape, or a whole added/removed slide. */
interface ReviewItem {
  key: string;
  kind: ItemKind;
  slideIndex: number;
  shapeName: string | null;
  shapeLabel: string;
  origText: string;
  modText: string;
  diffs: DiffHunk[];
  cellDiffs?: CellDiff[];
  isTableDiff: boolean;
}

interface Group {
  slide: SlideComparison;
  items: ReviewItem[];
}

/** Can this change be reverted in the live deck? Removed shapes/slides can't. */
function canRevert(item: ReviewItem): boolean {
  return item.kind !== "delete-shape" && item.kind !== "remove-slide";
}

/** Label for the "Revert all" button given the revertable / not-yet-reverted counts. */
function revertAllLabelFor(revertableCount: number, notReverted: number): string {
  if (revertableCount === 0) return "Nothing to revert";
  if (notReverted === 0) return "All reverted ✓";
  return `↺ Revert all (${notReverted})`;
}

function shapeKind(sr: ShapeResult): ItemKind {
  if (sr.status === "added") return "add-shape";
  if (sr.status === "deleted") return "delete-shape";
  return "modify";
}

function slideKey(c: SlideComparison): string {
  return `${c.status}:${c.origSlideIndex ?? "x"}:${c.modSlideIndex ?? "x"}`;
}

function slideNumberOf(c: SlideComparison): number {
  return c.modSlideNumber ?? c.origSlideNumber ?? 0;
}

function headerText(c: SlideComparison): string {
  if (c.status === "modified") {
    return `Slide ${c.origSlideNumber ?? "?"} → Slide ${c.modSlideNumber ?? "?"}`;
  }
  return `Slide ${slideNumberOf(c)}`;
}

function hasVisibleChange(sr: ShapeResult): boolean {
  return sr.diffs.some((h) => h.type !== "equal") || Boolean(sr.cellDiffs?.length);
}

/** Split each changed slide into per-shape items (modified) or a single whole-slide item. */
function buildGroups(changed: SlideComparison[]): Group[] {
  return changed.map((c) => {
    const key = slideKey(c);
    const slideIndex = c.modSlideIndex ?? c.origSlideIndex ?? 0;
    if (c.status === "modified") {
      const items = c.shapeResults.filter(hasVisibleChange).map((sr, i) => ({
        key: `${key}#${i}`,
        kind: shapeKind(sr),
        slideIndex,
        shapeName: sr.modShape?.name ?? sr.origShape?.name ?? null,
        shapeLabel: sr.origShape?.name ?? sr.modShape?.name ?? `Shape ${i + 1}`,
        origText: sr.origShape?.text ?? "",
        modText: sr.modShape?.text ?? "",
        diffs: sr.diffs,
        cellDiffs: sr.cellDiffs,
        isTableDiff: Boolean(sr.isTableDiff),
      }));
      return { slide: c, items };
    }
    return {
      slide: c,
      items: [
        {
          key: `${key}#slide`,
          kind: c.status === "added" ? "add-slide" : "remove-slide",
          slideIndex,
          shapeName: null,
          shapeLabel: c.status === "added" ? "New slide" : "Removed slide",
          origText: "",
          modText: "",
          diffs: [],
          isTableDiff: false,
        },
      ],
    };
  });
}

/** Diff-hunk type → CSS class (equal hunks are unstyled). */
const HUNK_CLASS: Partial<Record<DiffHunk["type"], string>> = {
  delete: styles.del,
  insert: styles.ins,
  moved: styles.moved,
};

/** Render diff hunks with strikethrough deletes / underlined inserts. */
function Hunks({ hunks }: { readonly hunks: DiffHunk[] }) {
  // Key each hunk by its running character offset — stable and unique within the
  // list without relying on the array index.
  let offset = 0;
  return (
    <>
      {hunks.map((h, i) => {
        const cls = HUNK_CLASS[h.type];
        const sep = i < hunks.length - 1 ? " " : "";
        const key = `${offset}:${h.type}`;
        offset += h.text.length + 1;
        return (
          <span key={key} className={cls}>
            {h.text}
            {sep}
          </span>
        );
      })}
    </>
  );
}

function ItemDiff({ item }: { readonly item: ReviewItem }) {
  if (item.isTableDiff && item.cellDiffs?.length) {
    return (
      <>
        {item.cellDiffs.map((cd) => (
          <div key={`r${cd.row}c${cd.col}`} className={styles.diffText}>
            <span className={styles.cellRef}>
              R{cd.row + 1}C{cd.col + 1}:
            </span>{" "}
            <Hunks hunks={cd.diffs} />
          </div>
        ))}
      </>
    );
  }
  return (
    <div className={styles.diffText}>
      <Hunks hunks={item.diffs} />
    </div>
  );
}

/** Stable key for a read-only shape row (avoids relying on the array index). */
function shapeResultKey(sr: ShapeResult): string {
  const name = sr.origShape?.name ?? sr.modShape?.name ?? "shape";
  return `${name}|${sr.diffs[0]?.text?.slice(0, 24) ?? sr.status}`;
}

function ShapeReadonly({ sr }: { readonly sr: ShapeResult }) {
  const name = sr.origShape?.name ?? sr.modShape?.name ?? "Shape";
  return (
    <div className={styles.shapeRow}>
      <div className={styles.shapeName}>▸ {name}</div>
      <div className={styles.diffText}>
        <Hunks hunks={sr.diffs} />
      </div>
    </div>
  );
}

function Tile({ n, label, tone }: { readonly n: number; readonly label: string; readonly tone: string }) {
  return (
    <div className={`${styles.tile} ${styles[tone] ?? ""}`}>
      <span className={styles.tileNum}>{n}</span>
      <span className={styles.tileLabel}>{label}</span>
    </div>
  );
}

interface CardProps {
  readonly slide: SlideComparison;
  readonly items: ReviewItem[];
  readonly renderActions: (item: ReviewItem) => ReactNode;
}

/** Expanded body of a slide card: per-shape rows (modified) or read-only shapes (added/removed). */
function SlideCardBody({ slide, items, renderActions }: CardProps) {
  if (slide.status === "modified") {
    return (
      <div className={styles.cardBody}>
        {items.map((item) => (
          <div key={item.key} className={styles.shapeRow}>
            <div className={styles.shapeName}>▸ {item.shapeLabel}</div>
            <ItemDiff item={item} />
            {renderActions(item)}
          </div>
        ))}
      </div>
    );
  }
  const shapes = slide.shapeResults.filter(hasVisibleChange);
  const first = items[0];
  return (
    <div className={styles.cardBody}>
      {shapes.length === 0 ? (
        <p className={styles.diffText}>{slide.status === "added" ? "New slide added." : "Slide removed."}</p>
      ) : (
        shapes.map((sr) => <ShapeReadonly key={shapeResultKey(sr)} sr={sr} />)
      )}
      {first && renderActions(first)}
    </div>
  );
}

/** One collapsible slide card: header (badge, title, change count, similarity) + body when open. */
function SlideCard({
  slide,
  items,
  isOpen,
  onToggle,
  renderActions,
}: CardProps & { readonly isOpen: boolean; readonly onToggle: () => void }) {
  const badge = BADGE[slide.status];
  return (
    <div className={styles.card}>
      <button type="button" className={styles.cardHead} onClick={onToggle} aria-expanded={isOpen}>
        <span className={styles.caret}>{isOpen ? "▾" : "▸"}</span>
        <span className={`${styles.badge} ${styles[badge.cls] ?? ""}`}>{badge.label}</span>
        <span className={styles.cardTitle}>{headerText(slide)}</span>
        <span className={styles.cardMeta}>
          <span className={styles.changeCount}>
            {items.length} {items.length === 1 ? "change" : "changes"}
          </span>
          {slide.status === "modified" && (
            <span className={styles.simPct}>{Math.round(slide.similarity * 100)}%</span>
          )}
        </span>
      </button>
      {isOpen && <SlideCardBody slide={slide} items={items} renderActions={renderActions} />}
    </div>
  );
}

/**
 * Compare Slides — diffs an uploaded original .pptx against the live deck and
 * lets the user review each change (per shape) and apply the decision back to
 * the open deck. Revert restores the original: edited text goes back, an added
 * shape/slide is deleted. Removed shapes/slides can't be re-added, so they're
 * review-only (Keep to acknowledge). Reverts that delete slides or shapes are
 * confirmed first.
 */
export function CompareSlides({ onBack }: Readonly<CompareSlidesProps>) {
  const { busy, error, report, compare, reset } = useCompareSlides();
  const [origName, setOrigName] = useState<string | null>(null);
  const [origSizeKb, setOrigSizeKb] = useState<number | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [tab, setTab] = useState<Tab>("all");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [sortDesc, setSortDesc] = useState(false);
  const [working, setWorking] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const changed = useMemo(() => {
    const list = (report?.slideComparisons ?? []).filter((c) => c.status !== "unchanged");
    return [...list].sort((a, b) =>
      sortDesc ? slideNumberOf(b) - slideNumberOf(a) : slideNumberOf(a) - slideNumberOf(b),
    );
  }, [report, sortDesc]);

  const groups = useMemo(() => buildGroups(changed), [changed]);
  const items = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const revertable = useMemo(() => items.filter(canRevert), [items]);

  const decisionOf = (key: string): Decision => decisions[key] ?? "pending";
  const total = items.length;
  const inState = (d: Decision) => items.filter((it) => decisionOf(it.key) === d).length;
  const tabCount = (t: Tab) => (t === "all" ? total : inState(t));
  const reviewedCount = total - inState("pending");
  const notRevertedCount = revertable.filter((it) => decisionOf(it.key) !== "reverted").length;
  const allKept = total > 0 && inState("kept") === total;

  // "Revert all" is disabled whenever the deck has slide-level additions or
  // removals — a single bulk click must never delete whole slides. Those must
  // be reverted one at a time (a deliberate, per-item action).
  const hasSlideLevelChange = items.some((it) => it.kind === "add-slide" || it.kind === "remove-slide");

  // The uploaded file shares no slides with the current deck — reverting the
  // added slides would wipe the whole current deck.
  const entirelyDifferent =
    report?.summary.matched === 0 && report.summary.totalOrig > 0 && report.summary.totalMod > 0;

  const acceptFile = async (file: File | undefined) => {
    reset();
    setDecisions({});
    setExpanded({});
    setTab("all");
    setNote(null);
    if (!file) {
      setOrigName(null);
      setOrigSizeKb(null);
      return;
    }
    if (!file.name.toLowerCase().endsWith(".pptx")) {
      setFileError("Please choose a PowerPoint (.pptx) file.");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileError(`That file is ${Math.round(file.size / (1024 * 1024))} MB. Please choose a .pptx up to 200 MB.`);
      return;
    }
    setFileError(null);
    setOrigName(file.name);
    setOrigSizeKb(Math.max(1, Math.round(file.size / 1024)));
    try {
      await compare(await file.arrayBuffer());
    } catch {
      setFileError("Couldn't read that file. Please try selecting it again.");
    }
  };

  /** Apply one decision to the live deck (best-effort; throws on API failure). */
  const applyToDeck = async (item: ReviewItem, decision: Decision): Promise<void> => {
    switch (item.kind) {
      case "modify":
        if (item.isTableDiff || !item.shapeName) {
          setNote("Reverting table cells in the deck isn't supported yet — marked as reviewed only.");
          return;
        }
        await setShapeText(item.slideIndex, item.shapeName, decision === "reverted" ? item.origText : item.modText);
        return;
      case "add-shape":
        // Revert deletes the newly added shape; Keep leaves it in place.
        if (decision === "reverted" && item.shapeName) await deleteShapeByName(item.slideIndex, item.shapeName);
        return;
      case "add-slide":
        if (decision === "reverted") await deleteSlideAt(item.slideIndex);
        return;
      case "delete-shape":
      case "remove-slide":
        // Re-adding a removed shape/slide isn't supported — review-only.
        return;
    }
  };

  const decide = async (item: ReviewItem, decision: Decision) => {
    setWorking(true);
    setNote(null);
    try {
      await applyToDeck(item, decision);
      setDecisions((prev) => ({ ...prev, [item.key]: decision }));
    } catch (err) {
      setNote(err instanceof Error ? err.message : String(err));
    } finally {
      setWorking(false);
    }
  };

  const decideAll = async (decision: Decision) => {
    // Revert only touches revertable items; Keep acknowledges everything.
    const targets = decision === "reverted" ? revertable : items;
    setWorking(true);
    setNote(null);
    try {
      if (decision === "reverted") {
        // Shape edits first (deck size unchanged), then slide deletes
        // high-index-first so earlier deletions don't shift later indices.
        const shapeOps = targets.filter((it) => it.kind !== "add-slide");
        const slideOps = targets
          .filter((it) => it.kind === "add-slide" && decisionOf(it.key) !== "reverted")
          .sort((a, b) => b.slideIndex - a.slideIndex);
        for (const it of shapeOps) await applyToDeck(it, decision);
        for (const it of slideOps) await applyToDeck(it, decision);
      } else {
        for (const it of targets) await applyToDeck(it, decision);
      }
      setDecisions((prev) => {
        const next = { ...prev };
        for (const it of targets) next[it.key] = decision;
        return next;
      });
    } catch (err) {
      setNote(err instanceof Error ? err.message : String(err));
    } finally {
      setWorking(false);
    }
  };

  const toggleExpandAll = () => {
    const anyCollapsed = groups.some((g) => !expanded[slideKey(g.slide)]);
    setExpanded(anyCollapsed ? Object.fromEntries(groups.map((g) => [slideKey(g.slide), true])) : {});
  };

  const visibleGroups = groups
    .map((g) => ({
      slide: g.slide,
      items: g.items.filter((it) => tab === "all" || decisionOf(it.key) === tab),
    }))
    .filter((g) => g.items.length > 0);

  const message = fileError ?? error;

  const revertAllLabel = revertAllLabelFor(revertable.length, notRevertedCount);

  const itemActions = (item: ReviewItem) => {
    const d = decisionOf(item.key);
    const allowRevert = canRevert(item);
    return (
      <div className={styles.itemActions}>
        {allowRevert && (
          <button
            type="button"
            className={`${styles.revertBtn} ${d === "reverted" ? styles.activeRevert : ""}`}
            onClick={() => void decide(item, "reverted")}
            disabled={working}
          >
            ↺ Revert
          </button>
        )}
        <button
          type="button"
          className={`${styles.keepBtn} ${d === "kept" ? styles.activeKeep : ""}`}
          onClick={() => void decide(item, "kept")}
          disabled={working}
        >
          ✓ Keep
        </button>
        {!allowRevert && (
          <span className={styles.cantRevert}>
            Can&apos;t revert — restoring a removed {item.kind === "remove-slide" ? "slide" : "shape"} isn&apos;t
            supported
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={styles.panel}>
      <ModuleHeader title="Compare Slides" subtitle="Deckorate" onBack={onBack} />

      <div className={styles.intro}>
        <h3 className={styles.heading}>Compare two versions of your deck</h3>
        <p className={styles.note}>
          Upload the <strong>original</strong> .pptx and we&apos;ll diff it against the active
          presentation. Review changes and revert or keep each one in the current deck.
        </p>
        <h4 className={styles.subheading}>Compare Text</h4>
        <p className={styles.hint}>
          Upload the <strong>original</strong> .pptx file to compare against the active presentation
          (modified version).
        </p>
        <p className={styles.tip}>
          <strong>Tip:</strong> Save your current file first — the comparison uses the last saved
          version of the active deck.
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pptx"
        className={styles.hiddenInput}
        onChange={(e) => void acceptFile(e.target.files?.[0])}
      />

      {origName ? (
        <p className={styles.uploaded}>
          Uploaded: <strong>{origName}</strong>
          {origSizeKb != null && ` (${origSizeKb} KB)`}{" "}
          <button
            type="button"
            className={styles.replace}
            onClick={() => inputRef.current?.click()}
            disabled={busy || working}
          >
            Replace
          </button>
        </p>
      ) : (
        <button
          type="button"
          className={`${styles.dropzone} ${dragOver ? styles.dropzoneActive : ""}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            void acceptFile(e.dataTransfer.files?.[0]);
          }}
        >
          <span className={styles.clip} aria-hidden="true">
            📎
          </span>
          <span className={styles.dropTitle}>Drop original .pptx here</span>
          <span className={styles.dropSub}>or click to browse</span>
        </button>
      )}

      {busy && (
        <div className={styles.busy}>
          <Spinner size="tiny" label="Comparing…" />
        </div>
      )}

      {message && (
        <p className={styles.error} role="alert">
          {message}
        </p>
      )}

      {report && !busy && (
        <div className={styles.results} role="status" aria-live="polite">
          <p className={styles.completeLine}>
            <strong>Comparison complete</strong> — {report.summary.totalOrig} vs{" "}
            {report.summary.totalMod} {report.summary.totalMod === 1 ? "slide" : "slides"},{" "}
            {report.summary.matched} matched.
          </p>

          {entirelyDifferent && (
            <p className={styles.warnBanner} role="alert">
              ⚠ This file doesn&apos;t look like a version of the current deck — none of its slides
              matched. Every slide shows as added or removed, so reverting the added slides would
              remove your current deck.
            </p>
          )}

          {total === 0 ? (
            <p className={styles.noDiff}>No text differences found between the two presentations.</p>
          ) : (
            <>
              <div className={styles.tiles}>
                <Tile n={total} label="Items" tone="tilePrimary" />
                <Tile n={report.summary.modified} label="Slides Changed" tone="tileAmber" />
                <Tile n={report.summary.added} label="Slides Added" tone="tileGreen" />
                <Tile n={report.summary.removed} label="Slides Removed" tone="tileRed" />
              </div>

              <div className={styles.tabs}>
                {(Object.keys(TAB_LABEL) as Tab[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`${styles.tab} ${tab === t ? styles.tabActive : ""}`}
                    onClick={() => setTab(t)}
                  >
                    {TAB_LABEL[t]} <span className={styles.tabCount}>{tabCount(t)}</span>
                  </button>
                ))}
              </div>

              <div className={styles.controls}>
                <button
                  type="button"
                  className={styles.ctrlBtn}
                  onClick={() => setSortDesc((v) => !v)}
                  title="Sort by slide number"
                  aria-label="Sort by slide number"
                >
                  ⇅
                </button>
                <button
                  type="button"
                  className={styles.ctrlBtn}
                  onClick={toggleExpandAll}
                  title="Expand / collapse all"
                  aria-label="Expand or collapse all"
                >
                  ⌄
                </button>
                <span className={styles.progressText}>
                  {reviewedCount} of {total} reviewed
                </span>
              </div>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${total ? Math.round((reviewedCount / total) * 100) : 0}%` }}
                />
              </div>

              {note && <p className={styles.note2}>{note}</p>}

              <div className={styles.cards}>
                {visibleGroups.map(({ slide, items: groupItems }) => {
                  const gkey = slideKey(slide);
                  return (
                    <SlideCard
                      key={gkey}
                      slide={slide}
                      items={groupItems}
                      isOpen={Boolean(expanded[gkey])}
                      onToggle={() => setExpanded((prev) => ({ ...prev, [gkey]: !prev[gkey] }))}
                      renderActions={itemActions}
                    />
                  );
                })}
                {visibleGroups.length === 0 && (
                  <p className={styles.emptyTab}>No {tab === "all" ? "" : tab} changes.</p>
                )}
              </div>

              <div className={styles.bulkBar}>
                {/* Wrapper carries the title so the tooltip still shows while the
                    button is disabled (browsers suppress title on disabled buttons). */}
                <span
                  className={styles.bulkRevertWrap}
                  title={
                    hasSlideLevelChange
                      ? "Option is disabled as there is slide level changes"
                      : undefined
                  }
                >
                  <button
                    type="button"
                    className={styles.bulkRevert}
                    onClick={() => void decideAll("reverted")}
                    disabled={working || notRevertedCount === 0 || hasSlideLevelChange}
                  >
                    {revertAllLabel}
                  </button>
                </span>
                <button
                  type="button"
                  className={styles.bulkKeep}
                  onClick={() => void decideAll("kept")}
                  disabled={working || allKept}
                >
                  {allKept ? "All kept ✓" : "✓ Keep all"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
